import ActionDialog from "@dashboard/components/ActionDialog/ActionDialog";
import { type ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton/ConfirmButton";
import { type AtticErrorFragment } from "@dashboard/graphql";
import { Box, Input, Text } from "@saleor/macaw-ui-next";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { getAtticInventoryErrorMessage } from "../../errors";
import { stockMessages } from "../../messages";
import { parseCost, parseInteger } from "../../utils";

export interface AtticAdjustInput {
  location: string;
  delta: number;
  reason: string;
  unitCost: number | null;
}

export interface AtticAdjustDialogProps {
  open: boolean;
  /** The location most of the stock sits in, as the default. */
  defaultLocation: string;
  errors: AtticErrorFragment[];
  confirmButtonState: ConfirmButtonTransitionState;
  onClose: () => void;
  onConfirm: (input: AtticAdjustInput) => void;
}

/** A count correction: copies found (+) or gone (−) at one location, with a reason. */
export const AtticAdjustDialog = ({
  open,
  defaultLocation,
  errors,
  confirmButtonState,
  onClose,
  onConfirm,
}: AtticAdjustDialogProps) => {
  const intl = useIntl();
  const [location, setLocation] = useState(defaultLocation);
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [unitCost, setUnitCost] = useState("");

  useEffect(() => {
    if (open) {
      setLocation(defaultLocation);
      setDelta("");
      setReason("");
      setUnitCost("");
    }
  }, [open, defaultLocation]);

  const parsedDelta = parseInteger(delta);
  const costBlank = unitCost.trim() === "";
  const parsedCost = costBlank ? null : parseCost(unitCost);
  const costValid = costBlank || parsedCost !== null;
  const valid =
    location.trim() !== "" &&
    parsedDelta !== null &&
    parsedDelta !== 0 &&
    reason.trim() !== "" &&
    costValid;
  const [refusal] = errors;

  return (
    <ActionDialog
      open={open}
      title={intl.formatMessage(stockMessages.adjustTitle)}
      confirmButtonState={confirmButtonState}
      disabled={!valid}
      onClose={onClose}
      onConfirm={() => {
        if (parsedDelta !== null && valid) {
          onConfirm({
            location: location.trim().toUpperCase(),
            delta: parsedDelta,
            reason: reason.trim(),
            unitCost: parsedDelta > 0 ? parsedCost : null,
          });
        }
      }}
    >
      <Box display="flex" flexDirection="column" gap={4}>
        <Input
          label={intl.formatMessage(stockMessages.adjustLocation)}
          value={location}
          onChange={event => setLocation(event.target.value)}
          data-test-id="attic-adjust-location"
        />
        <Input
          type="number"
          label={intl.formatMessage(stockMessages.adjustDelta)}
          value={delta}
          onChange={event => setDelta(event.target.value)}
          error={delta !== "" && (parsedDelta === null || parsedDelta === 0)}
          data-test-id="attic-adjust-delta"
        />
        <Input
          label={intl.formatMessage(stockMessages.adjustReason)}
          value={reason}
          onChange={event => setReason(event.target.value)}
          data-test-id="attic-adjust-reason"
        />
        {parsedDelta !== null && parsedDelta > 0 && (
          <Input
            label={intl.formatMessage(stockMessages.adjustUnitCost)}
            value={unitCost}
            onChange={event => setUnitCost(event.target.value)}
            error={!costValid}
            data-test-id="attic-adjust-unit-cost"
          />
        )}
        {refusal && (
          <Text size={3} color="critical1" data-test-id="attic-adjust-error">
            {getAtticInventoryErrorMessage(refusal, intl)}
          </Text>
        )}
      </Box>
    </ActionDialog>
  );
};
